// Partner Cabinet API helpers — cookie-based auth + CSRF double-submit

const BASE = (import.meta.env.VITE_API_URL || '/api/v1') + '/partner';

// CSRF-токен держим в памяти (переустанавливается при login и при getMe)
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) || {}),
  };
  if (method !== 'GET' && method !== 'HEAD' && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
  if (res.status === 401) {
    csrfToken = null;
    // Редирект только если мы не уже на login-странице
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/partner/login')) {
      window.location.href = '/partner/login';
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PartnerBalance {
  total_usd: number;
  locked_usd: number;
  available_usd: number;
}

export interface ErrorInfo {
  error_code: string;
  label: string;
  type: 'system' | 'user_action' | 'unknown';
  hint: string;
}

export interface ActiveOrder {
  status: string;
  status_label: string;
  steam_trade_offer_id?: string | null;
  error_code?: string | null;
  error_info?: ErrorInfo | null;
}

export interface Purchase {
  id: string;
  custom_id?: string | null;
  product: string | null;
  purchase_status: string;
  purchase_status_label: string;
  purchase_status_color: string;
  max_price_usd: number | null;
  exec_price_usd?: number | null;
  active_order?: ActiveOrder | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_sync_at?: string | null;
  /** Полный ответ Xpanda по реквесту. Приходит только при fresh=1. */
  raw?: Record<string, unknown> | null;
}

export interface DashboardKpis {
  active_count: number;
  done_count_30d: number;
  error_count_30d: number;
  success_rate_30d: number | null;
}

export interface VolumePoint {
  date: string;
  count: number;
  total_usd?: number;
  sum_usd?: number;
}

export interface StatusBucket {
  status: string;
  label: string;
  color: string;
  count: number;
}

export interface DashboardData {
  balance: PartnerBalance;
  kpis: DashboardKpis;
  recent_active: Purchase[];
  recent_events: Purchase[];
  volume_by_day: VolumePoint[];
  status_distribution: StatusBucket[];
}

export interface BalanceData extends PartnerBalance {
  active_locked_usd: number;
  formula_note: string;
}

export interface PurchasesListData {
  items: Purchase[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface StatsSummary {
  total_purchases: number;
  total_spent_usd: number;
  savings_usd: number;
  avg_exec_price_usd: number;
  success_rate: number | null;
}

export interface AvgPricePoint {
  date: string;
  avg_usd: number | null;
}

export interface StatsData {
  period: string;
  date_from: string;
  date_to: string;
  summary: StatsSummary;
  volume_by_day: VolumePoint[];
  avg_price_by_day: AvgPricePoint[];
  error_distribution: Array<{ error_code: string; count: number }>;
  top_items_by_count: Array<{ product: string; count: number }>;
  top_items_by_volume: Array<{ product: string; sum_usd: number }>;
}

export interface ProfileData {
  key_last4: string;
  key_first_used_at: string | null;
  last_login_at: string | null;
  last_login_ip: string | null;
  recent_events: Array<{
    event_type: string;
    ip: string | null;
    user_agent: string | null;
    created_at: string | null;
  }>;
}

export interface MeData {
  authenticated: boolean;
  key_last4: string;
  session_expires_at: string | null;
  csrf_token: string;
}

export interface RefreshUnfinishedResult {
  ok: boolean;
  checked: number;
  updated: number;
  failed: number;
  /** Не успели за отведённое время — подхватит фоновый поллер. */
  timed_out: number;
  still_unfinished: number;
}

export interface PurchasesListParams {
  page?: number;
  page_size?: number;
  status?: string[];
  order_status?: string[];
  date_from?: string;
  date_to?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const partnerApi = {
  login: async (api_key: string): Promise<{ csrf_token: string; balance: PartnerBalance; partner: { key_last4: string } }> => {
    const res = await fetch(`${BASE}/auth`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ api_key }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Неверный API-ключ');
    }
    const data = await res.json();
    csrfToken = data.csrf_token || null;
    return data;
  },

  logout: async (): Promise<void> => {
    await request('/auth', { method: 'DELETE' }).catch(() => {});
    csrfToken = null;
  },

  me: async (): Promise<MeData | null> => {
    try {
      const me = await request<MeData>('/me');
      if (me.csrf_token) csrfToken = me.csrf_token;
      return me;
    } catch {
      return null;
    }
  },

  getDashboard: () => request<DashboardData>('/dashboard'),

  getBalance: () => request<BalanceData>('/balance'),

  getPurchases: (params: PurchasesListParams = {}) => {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    if (params.status?.length) q.set('status', params.status.join(','));
    if (params.order_status?.length) q.set('order_status', params.order_status.join(','));
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.search) q.set('search', params.search);
    if (params.sort) q.set('sort', params.sort);
    if (params.order) q.set('order', params.order);
    const qs = q.toString();
    return request<PurchasesListData>(`/purchases${qs ? `?${qs}` : ''}`);
  },

  // fresh=true — перечитать реквест в Xpanda и получить его полный ответ в поле raw.
  getPurchaseDetail: (id: string, fresh = false) =>
    request<Purchase>(`/purchases/${encodeURIComponent(id)}${fresh ? '?fresh=1' : ''}`),

  refreshUnfinished: () =>
    request<RefreshUnfinishedResult>('/purchases/refresh-unfinished', { method: 'POST' }),

  getStats: (period: string, date_from?: string, date_to?: string) => {
    const q = new URLSearchParams({ period });
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    return request<StatsData>(`/stats?${q.toString()}`);
  },

  getProfile: () => request<ProfileData>('/profile'),

  logoutAll: () => request('/logout-all', { method: 'POST' }),
};
